import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { MasterControlIngestionPipeline, MasterControlIngestionState, MasterControlSyncDryRun } from "@/hooks/useMasterControl";
import MCIngestionCard from "./MCIngestionCard";
import MCIngestionDetailsDrawer from "./MCIngestionDetailsDrawer";
import DryRunSyncModal from "./DryRunSyncModal";

type MCIngestionDashboardProps = {
  pipelines: MasterControlIngestionState;
  loading: boolean;
  onRefresh: () => Promise<void>;
  onRunFull: () => Promise<void>;
  dryRunSync: () => Promise<MasterControlSyncDryRun>;
  canRunSync?: boolean;
  canDryRun?: boolean;
};

export function MCIngestionDashboard({
  pipelines,
  loading,
  onRefresh,
  onRunFull,
  dryRunSync,
  canRunSync = true,
  canDryRun = true,
}: MCIngestionDashboardProps) {
  const [selected, setSelected] = useState<MasterControlIngestionPipeline | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [dryRunOpen, setDryRunOpen] = useState(false);
  const [dryRunPreview, setDryRunPreview] = useState<MasterControlSyncDryRun | null>(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);

  const runFullSync = async () => {
    if (!canRunSync) return;
    setRunning(true);
    try {
      await onRunFull();
      await onRefresh();
    } finally {
      setRunning(false);
    }
  };

  const openDryRun = async () => {
    if (!canDryRun) return;
    setDryRunLoading(true);
    try {
      const payload = await dryRunSync();
      setDryRunPreview(payload);
      setDryRunOpen(true);
    } catch {
      /* toast handled upstream */
    } finally {
      setDryRunLoading(false);
    }
  };

  const pipelinesList = pipelines ?? [];

  return (
    <Card className="rounded-2xl border border-border bg-background/60 shadow-none">
      <CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <CardTitle className="text-lg font-semibold text-foreground">Ingestion Pipeline Dashboard</CardTitle>
          <p className="text-sm text-muted-foreground">
            Pending job counts, last execution timestamps, and error indicators for every ingestion surface.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => void onRefresh()} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </Button>
          <Button onClick={() => void runFullSync()} size="sm" disabled={running || !canRunSync}>
            {running ? "Running…" : "Run full sync"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void openDryRun()}
            disabled={dryRunLoading || !canDryRun}
            className="border border-border text-foreground/80 hover:text-primary-foreground dark:text-amber-200"
            title={canDryRun ? undefined : "Upgrade to unlock dry run access"}
          >
            {dryRunLoading ? "Loading…" : "Dry run sync"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {pipelinesList.length === 0 ? (
          <p className="text-sm text-muted-foreground">No ingestion pipelines reported.</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {pipelinesList.map((pipeline) => (
              <MCIngestionCard
                key={pipeline.id}
                pipeline={pipeline}
                onSelect={(entry) => {
                  setSelected(entry);
                  setDrawerOpen(true);
                }}
              />
            ))}
          </div>
        )}
      </CardContent>

      <MCIngestionDetailsDrawer
        pipeline={selected}
        open={drawerOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDrawerOpen(false);
            setSelected(null);
          } else {
            setDrawerOpen(true);
          }
        }}
        onRun={async (_pipeline) => {
          await runFullSync();
          setDrawerOpen(false);
          setSelected(null);
        }}
        running={running}
        canRun={canRunSync}
      />

      <DryRunSyncModal
        open={dryRunOpen}
        preview={dryRunPreview}
        onOpenChange={(open) => setDryRunOpen(open)}
        onConfirm={async () => {
          setDryRunOpen(false);
        }}
        confirming={false}
      />
    </Card>
  );
}

export default MCIngestionDashboard;
